import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Button, Typography } from "@mui/material";

import { formatDate } from "@aclymatepackages/formatters";

import Link from "../../../atoms/mui/Link";
import ExplorerProductDetailsSlider from "../../views/ExplorerProductDetailsSlider";

import {
  useCachedDisplayData,
  useCollectionDataListener
} from "../../../../helpers/firebase";
import { ProductsEmissionsFactorsContextProvider } from "../../../../helpers/contexts/ProductsEmissionsFactors";

import CollectionScreen, { StatusChip } from "./CollectionScreen";

const PRODUCT_VIEW_HREF = (productId) =>
  `/explorer/dashboard?view=products&selectedId=${productId}`;

const mergeProducts = (vendorAssigned, ownCatalog) => {
  const ownIds = new Set((ownCatalog || []).map(({ id }) => id));
  const dedupedVendorAssigned = (vendorAssigned || []).filter(
    ({ id }) => !ownIds.has(id)
  );
  return [
    ...(ownCatalog || []).map((product) => ({ ...product, source: "own" })),
    ...dedupedVendorAssigned.map((product) => ({
      ...product,
      source: "vendor-assigned"
    }))
  ];
};

const buildEventByProductId = (lifecycleEvents) =>
  Object.fromEntries(
    (lifecycleEvents || []).map((event) => [event.productId, event])
  );

const buildScoutResolvedComponents = (scoutComponents) =>
  (scoutComponents || [])
    .filter(({ type }) => type)
    .map(({ type, productWeight }, idx) => ({
      id: `scout-component-${idx}`,
      name: type,
      materials: [
        { type, weight: productWeight || 0, weightUnit: "g" }
      ],
      productWeight: productWeight || 0
    }));

const buildScoutPackaging = (scoutPackaging) =>
  (scoutPackaging || [])
    .filter(({ type }) => type)
    .map(({ type, packagingWeight }) => ({
      type,
      weight: packagingWeight || 0,
      weightUnit: "g"
    }));

const buildScoutRoute = (scoutRoute) => {
  if (!scoutRoute || scoutRoute.destinations) {
    return scoutRoute;
  }
  const { origin, destination, mode } = scoutRoute;
  if (!origin && !destination && !mode) {
    return scoutRoute;
  }
  return {
    destinations: [
      { name: origin || "", label: origin || "" },
      { name: destination || "", label: destination || "" }
    ],
    legs: [{ transportationMethod: mode || "" }]
  };
};

const buildSelectedProduct = ({
  selectedId,
  ownCatalog,
  vendorAssigned,
  ownLifecycleEvents,
  vendorAssignedLifecycleEvents
}) => {
  if (!selectedId) {
    return null;
  }
  const allProductDocs = [...(ownCatalog || []), ...(vendorAssigned || [])];
  const productDoc = allProductDocs.find(({ id }) => id === selectedId);
  if (!productDoc) {
    return null;
  }
  const vendorLifecycleEvent = (vendorAssignedLifecycleEvents || []).find(
    ({ productId }) => productId === selectedId
  );
  const ownLifecycleEvent = (ownLifecycleEvents || []).find(
    ({ productId }) => productId === selectedId
  );
  const lifecycleEvent = vendorLifecycleEvent || ownLifecycleEvent || {};
  const isOwnProduct = !vendorLifecycleEvent && !!ownLifecycleEvent;

  const refResolvedComponents = (productDoc.componentRefs || [])
    .map(({ id: componentId }) =>
      allProductDocs.find((doc) => doc.id === componentId)
    )
    .filter(Boolean);

  const ownBomOverlay = {
    ...(isOwnProduct
      ? {
          resolvedComponents: refResolvedComponents.length
            ? refResolvedComponents
            : buildScoutResolvedComponents(lifecycleEvent.components),
          packaging: buildScoutPackaging(lifecycleEvent.packaging)
        }
      : { resolvedComponents: refResolvedComponents }),
    route: buildScoutRoute(lifecycleEvent.route)
  };

  return {
    ...productDoc,
    ...lifecycleEvent,
    ...ownBomOverlay,
    id: productDoc.id,
    lifecycleId: lifecycleEvent.id,
    isReceived: false
  };
};

const buildCustomerNameMap = (customers) =>
  Object.fromEntries(
    (customers || []).map(({ buyerId, buyerName }) => [buyerId, buyerName])
  );

const STATUS_BY_BOM = {
  completed: { label: "Completed", tone: "success", action: "View" },
  confirmed: { label: "Confirmed", tone: "success", action: "View" },
  requested: { label: "Requested", tone: "info", action: "Respond" },
  pending: { label: "Pending", tone: "neutral", action: "Continue" },
  declined: { label: "Declined", tone: "neutral", action: "Review" }
};

const NO_DATA_STATUS = {
  label: "No data",
  tone: "neutral",
  action: "Add data"
};

const buildProductRow = ({ product, event, customerNames }) => {
  const status =
    (event?.bomStatus && STATUS_BY_BOM[event.bomStatus]) || NO_DATA_STATUS;
  const requestedBy =
    customerNames[event?.ownerId] || event?.ownerName || product?.ownerName || "—";

  return {
    id: product.id,
    name: product.name || "Unnamed product",
    type: product.source === "own" ? "Own catalog" : "From supplier",
    statusLabel: status.label,
    statusTone: status.tone,
    requestedBy,
    updated: event?.dateRequested ? formatDate(event.dateRequested) : "—",
    action: status.action
  };
};

const countByTone = (rows, tone) =>
  rows.filter((row) => row.statusTone === tone).length;

const COLUMNS = [
  { key: "name", label: "Product" },
  { key: "type", label: "Source" },
  {
    key: "status",
    label: "Status",
    render: (row) => <StatusChip label={row.statusLabel} tone={row.statusTone} />
  },
  { key: "requestedBy", label: "Requested by" },
  { key: "updated", label: "Last activity" },
  {
    key: "action",
    label: "",
    align: "right",
    render: (row) => (
      <Link href={PRODUCT_VIEW_HREF(row.id)}>
        <Button color="primary" size="small">
          <Typography variant="caption" color="primary">
            {row.action}
          </Typography>
        </Button>
      </Link>
    )
  }
];

const ProductsTab = () => {
  const [ownCatalog] = useCachedDisplayData("product-components");
  const [vendorAssigned] = useCachedDisplayData("vendor-assigned-products");
  const [vendorAssignedLifecycleEvents] = useCachedDisplayData(
    "vendor-assigned-product-lifecycle-events"
  );
  const [ownLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const [customers] = useCollectionDataListener("customers");

  const location = useLocation();
  const history = useHistory();
  const selectedId = new URLSearchParams(location.search).get("selectedId");

  const merged = mergeProducts(vendorAssigned, ownCatalog).filter(
    (p) => !p?.archived && !p?.parentProductId
  );
  const eventByProductId = {
    ...buildEventByProductId(ownLifecycleEvents),
    ...buildEventByProductId(vendorAssignedLifecycleEvents)
  };
  const customerNames = buildCustomerNameMap(customers);

  const rows = merged.map((product) =>
    buildProductRow({
      product,
      event: eventByProductId[product.id],
      customerNames
    })
  );

  const confirmed = countByTone(rows, "success");
  const pending = countByTone(rows, "info");
  const noData = countByTone(rows, "neutral");

  const stats = [
    { label: "Total products", value: rows.length, tone: "total" },
    { label: "Confirmed", value: confirmed, tone: "success" },
    { label: "BOMs awaiting acceptance", value: pending, tone: "warning" },
    { label: "Missing BOM data", value: noData, tone: "neutral" }
  ];

  const statusSegments = [
    { label: "Confirmed", value: confirmed, tone: "success" },
    { label: "Awaiting acceptance", value: pending, tone: "info" },
    { label: "Missing BOM data", value: noData, tone: "neutral" }
  ];

  const selectedProduct = useMemo(
    () =>
      buildSelectedProduct({
        selectedId,
        ownCatalog,
        vendorAssigned,
        ownLifecycleEvents,
        vendorAssignedLifecycleEvents
      }),
    [
      selectedId,
      ownCatalog,
      vendorAssigned,
      ownLifecycleEvents,
      vendorAssignedLifecycleEvents
    ]
  );

  const clearSelectedId = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedId");
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : ""
    });
  };

  const setSelectedProduct = (nextProduct) => {
    if (nextProduct) {
      return;
    }
    clearSelectedId();
  };

  return (
    <>
      <CollectionScreen
        heading="My Product BOM Requests"
        subtitle="Respond to bill-of-materials confirmation requests from your customers."
        stats={stats}
        statusTitle="Product data status"
        statusSegments={statusSegments}
        tableTitle="Products"
        columns={COLUMNS}
        rows={rows}
        emptyText="No BOM requests yet. This screen unlocks once a customer sends you one."
      />
      {selectedProduct && (
        <ProductsEmissionsFactorsContextProvider>
          <ExplorerProductDetailsSlider
            selectedProduct={selectedProduct}
            setSelectedProduct={setSelectedProduct}
          />
        </ProductsEmissionsFactorsContextProvider>
      )}
    </>
  );
};

export default ProductsTab;
